import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ExternalLink } from '@/components/ExternalLink';
import { trackExternalCtaClick } from '@/lib/analytics';

const AppointmentCTA = () => {
  return (
    <section className="py-24 lg:py-32 bg-background relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.015] bg-[radial-gradient(circle_at_50%_50%,_hsl(var(--foreground))_1px,_transparent_1px)] bg-[length:32px_32px]" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Decorative Element */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="w-12 h-px bg-gradient-to-r from-transparent to-brass/40" />
            <span className="w-2 h-2 bg-brass/40 rotate-45" />
            <span className="w-12 h-px bg-gradient-to-l from-transparent to-brass/40" />
          </div>
          
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-foreground tracking-wide">
            Experience the Museum
          </h2>
          
          <p className="mt-8 font-serif text-lg text-muted-foreground leading-relaxed italic max-w-2xl mx-auto">
            The Y.O. Ranch Headquarters Texas History Museum offers private, appointment-only access 
            to registered ranch guests. Experience our collections with personalized attention 
            and scholarly interpretation.
          </p>
          
          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-16 mb-16">
            {[
              { title: 'By Appointment', subtitle: 'Scheduled viewings only' },
              { title: 'Private Access', subtitle: 'For registered guests' },
              { title: 'Personalized Tours', subtitle: 'Curator-led experiences' },
            ].map((feature, index) => (
              <div 
                key={feature.title}
                className={cn(
                  "text-center group",
                  index === 1 && "sm:border-x sm:border-border sm:px-8"
                )}
              >
                <p className="font-sans text-xs tracking-widest uppercase text-brass mb-2 transition-transform duration-300 group-hover:-translate-y-0.5">
                  {feature.title}
                </p>
                <p className="font-serif text-sm text-muted-foreground italic">
                  {feature.subtitle}
                </p>
              </div>
            ))}
          </div>
          
          {/* CTA */}
          <ExternalLink
            href="https://yoranchheadquarters.com"
            onClick={() => trackExternalCtaClick('https://yoranchheadquarters.com')}
            className="inline-flex items-center gap-3 font-sans text-sm tracking-widest uppercase text-foreground border border-border px-8 py-5 hover:border-brass hover:text-brass transition-all duration-300 group hover:shadow-[0_4px_20px_-8px_hsl(var(--brass)/0.3)]"
          >
            Book a Visit, Tour, or Room
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" aria-hidden="true" />
          </ExternalLink>

          <p className="mt-6 font-sans text-xs tracking-wider text-muted-foreground">
            Already a registered guest?{' '}
            <Link
              to="/request-appointment"
              className="text-brass hover:text-brass/80 underline underline-offset-4 transition-colors"
            >
              Request a museum appointment
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default AppointmentCTA;